<template>
  <DoughnutChart
    ref="chartRef"
    :data="data"
    :options="optionsWithDefaults"
    @click="handleChartClick"
  />
</template>

<script lang="js">
import { defineComponent, ref } from "vue";
import { Doughnut, getElementsAtEvent } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import defaultsDeep from "lodash/defaultsDeep";
import { COLORS } from "@/constants/colors.ts";

// The wrap width the centre text used before it was made configurable; kept as the default so
// existing callers render identically.
const DEFAULT_INSIDE_TEXT_MAX_WIDTH = 150;

ChartJS.register({
  id: "doughnutCenterText",
  beforeDraw(chart) {
    const { width } = chart;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;

    // Takes the context (with font already set), text, and max width.
    // Returns an array of strings (lines).
    const getLines = (ctx, text, maxWidth) => {
      if (!text) return [];
      const words = text.split(" ");
      let line = "";
      const lines = [];

      words.forEach((word) => {
        const testLine = line + word + " ";
        const testWidth = ctx.measureText(testLine).width;
        if (testWidth > maxWidth && line.length > 0) {
          lines.push(line.trim());
          line = word + " ";
        } else {
          line = testLine;
        }
      });
      lines.push(line.trim());
      return lines;
    };

    // The doughnut's cutout radius in pixels, once the arcs have been laid out. Absent before the
    // first layout pass — and the method itself is absent when this plugin is driven with a
    // hand-built chart object, as the spec does — which is why every use of it falls back.
    const innerRadius = chart.getDatasetMeta?.(0)?.data?.[0]?.innerRadius;

    if (chart.config.options.insideTextData) {
      // `maxWidth` is optional: it defaults to the value this plugin always used, so a caller
      // that does not pass one keeps its current wrapping. Callers whose doughnut is smaller or
      // larger than the size that 150 was chosen for need to say so, or the text wraps at a
      // width the hole cannot contain and spills over the stroke.
      const {
        title,
        subtitle,
        thirdLine,
        offsetY = 0,
        maxWidth = DEFAULT_INSIDE_TEXT_MAX_WIDTH,
      } = chart.config.options.insideTextData;

      // --- Truncate all text fields if they are too long ---
      const displayTitle = title && title.length > 50 ? title.slice(0, 50).trim() + "..." : title;
      let displaySubtitle = subtitle;
      let displayThirdLineText = thirdLine?.text;

      // Subtitle Truncation
      if (subtitle && subtitle.length > 50) {
        displaySubtitle = subtitle.slice(0, 50).trim() + "...";
      }

      // Third Line Truncation
      if (thirdLine && thirdLine.text && thirdLine.text.length > 40) {
        displayThirdLineText = thirdLine.text.slice(0, 40).trim() + "...";
      }

      // --- Subtitle Word Wrapping ---
      const lineHeight = 20;

      // Calculate Subtitle Lines
      ctx.font = "600 16px Source Sans Pro";
      const subtitleLines = getLines(ctx, displaySubtitle, maxWidth);

      // Calculate Third Line Lines
      const thirdLineFont = thirdLine?.font || "600 16px Source Sans Pro";
      ctx.font = thirdLineFont;
      const thirdLineLines = getLines(ctx, displayThirdLineText, maxWidth);

      // --- Positioning ---
      // Dynamic height based on how many lines were generated for BOTH sections
      const subtitleHeight = subtitleLines.length * lineHeight;
      const thirdLineHeight = thirdLineLines.length * lineHeight;

      // 28 is roughly title height/spacing
      const totalContentHeight = 28 + subtitleHeight + (thirdLine ? thirdLineHeight : 0);
      let currentY = (top + bottom) / 2 - totalContentHeight / 2 + offsetY;
      const x = width / 2;

      // Draw ---

      // Draw Title
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = COLORS.PRIMARY;
      ctx.font = "500 48px Quicksand";
      // Bounded by the square the hole inscribes, measured off the arc rather than taken from the
      // caller: `innerRadius` is the cutout in pixels, and a square inside a circle of radius r is
      // r * sqrt(2) a side. The title was truncated at 50 CHARACTERS but never constrained by
      // WIDTH, so at 48px a total like "1,234,567" (~240px) ran straight over the stroke of a
      // 189px hole. `fillText`'s fourth argument condenses to fit instead of overflowing, and
      // deriving it here means it stays correct at every chart size with no caller involvement.
      const titleMaxWidth = innerRadius ? innerRadius * Math.SQRT2 : maxWidth;
      ctx.fillText(displayTitle, x, currentY, titleMaxWidth);

      // Draw Subtitle
      currentY += 40; // Spacing after title
      ctx.fillStyle = COLORS.PRIMARY;
      ctx.font = "600 16px Source Sans Pro";
      subtitleLines.forEach((line, index) => {
        ctx.fillText(line, x, currentY + index * lineHeight);
      });

      // Draw Third Line
      if (thirdLine && thirdLine.text) {
        // Move Y down by the total height of the subtitle block
        currentY += subtitleHeight;

        ctx.fillStyle = thirdLine.color || COLORS.PRIMARY;
        ctx.font = thirdLineFont;

        thirdLineLines.forEach((line, index) => {
          ctx.fillText(line, x, currentY + index * lineHeight);
        });
      }
    }
  },
});

// Register Chart.js modules
ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale);

export default defineComponent({
  components: {
    DoughnutChart: Doughnut,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: false,
    },
  },
  setup(props, { emit }) {
    const chartRef = ref(null);
    const defaultOptions = {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label(tooltipItem) {
              return tooltipItem.label;
            },
            title: () => null,
          },
          caretSize: 0,
          backgroundColor: COLORS.PRIMARY,
          bodyFontColor: COLORS.WHITE,
          bodyFontSize: 14,
          displayColors: false,
        },
      },
      layout: {
        padding: 10,
      },
      elements: {
        arc: {
          borderWidth: 0,
        },
      },
      hoverOffset: 5,
      onHover: (event, activeElements) => {
        if (activeElements?.length > 0) {
          event.native.target.style.cursor = "pointer";
        } else {
          event.native.target.style.cursor = "auto";
        }
      },
    };

    const handleChartClick = (event) => {
      const chart = chartRef.value?.chart;
      if (!chart) {
        return;
      }

      const elements = getElementsAtEvent(chart, event);
      const element = elements?.[0];
      if (!element) {
        return;
      }

      emit("on-chart-click", element.index);
    };

    return {
      chartRef,
      handleChartClick,
      optionsWithDefaults: defaultsDeep(props.options, defaultOptions),
    };
  },
});
</script>
